import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import TuneIcon from "@mui/icons-material/Tune";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningIcon from "@mui/icons-material/Warning";
import { Tooltip, Alert, Button, Modal, Box } from "@mui/material";
import { toast } from "react-toastify";
import getColumns from "./column.js";
import { label } from "yet-another-react-lightbox";
import DynamicTable from "../../../components/common/DynamicTable.jsx";
import useModal from "../../../hooks/useModal.js";
import useAuthUser from "../../../hooks/useAuthUser.js";
import AddCustomField from "./AddCustomField.jsx";
import usePaginatedList from "../../../hooks/usePaginatedList.js";
import { deleteCustomField, getCustomFieldsList } from "../../../services/customField.js";
import useConfirm from "../../../hooks/useConfirm.js";
import FilterPanel from "../../../components/common/AdvanceFilter.jsx";

const CustomFieldList = () => {
  const navigate = useNavigate();
  const { isAdminHrRole } = useAuthUser();
  const { confirm } = useConfirm();
  const { showModal, hideModal } = useModal();
  const [showFilter, setShowFilter] = useState(false);
  const [fields, setFields] = useState([
    { key: 'search', label: "Field Label", type: 'text' },
    { key: 'create_date', label: 'Created Date', type: 'daterange' },
    { key: 'field_type', label: 'Field Type', type: 'multiselect', options: [{ id: 1, label: "text", value: "text" }] },
    { key: 'module_name', label: 'Module Name', type: 'multiselect', options: [{ id: 1, label: "timesheet", value: "timesheet" }] },
  ]);

  const fetchData = async (data) => {
    return await getCustomFieldsList(data)
  }

  const {
    data,
    loading,
    error,
    pagination,
    setPage,
    setItemsPerPage,
    setFilters,
    refresh
  } = usePaginatedList(fetchData);

  const handleCreateCustomField = () => {
    showModal("Add Custom Field", <AddCustomField refresh={refresh} />);
  };

  const handleEditCustomField = (existingField) => {
    showModal("Edit Custom Field", <AddCustomField refresh={refresh} existingField={existingField} />);
  };

  const handleDelete = async (id) => {
    try {
      const isConfirmed = await confirm({
        title: "Confirm Request Approval",
        content: `Are you sure you want to delete this field?`,
        confirmText: "Yes, Delete",
        cancelText: "Cancel"
      });
      if (!isConfirmed) return;

      await deleteCustomField(id)
      toast.success("Field deleted successfully!");
      refresh();
    }
    catch (error) {
      console.log("Error in delete field");
      toast.error("Error in delete field");
    }
  }

  if (!isAdminHrRole) return null;

  return (
    <>
      {/* Custom Header */}
      <div className="bg-light mb-2">
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">

            {/* Left Section: Page Size Dropdown */}
            <div className="d-flex align-items-center gap-2">
              <label className="mb-0 text-muted small">Show</label>
              <select
                className="form-select form-select-sm"
                style={{ width: "80px" }}
                value={pagination?.itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-muted small">entries</span>
            </div>

            {/* Right Section: Action Buttons */}
            <div className="d-flex gap-2 flex-wrap">

              <button
                className="btn btn-success btn-sm d-flex align-items-center"
                onClick={() => handleCreateCustomField()}
              >
                <AddIcon sx={{ fontSize: 18 }} className="me-1" />
                <span className="d-none d-sm-inline">Create Field</span>
              </button>

              <Tooltip title="Filter">
                <button
                  className="btn btn-outline-dark btn-sm"
                  onClick={() => setShowFilter(!showFilter)}
                >
                  <TuneIcon sx={{ fontSize: 18 }} />
                </button>
              </Tooltip>

            </div>
          </div>
        </div>
      </div>

      <FilterPanel
        fields={fields}
        isOpen={showFilter}
        onClose={() => setShowFilter(false)}
        onApply={(vals) => {
          setFilters({ filters: vals })
        }}
        onClear={() => { setFilters({ filters: {}}) }}
      />

      <DynamicTable
        columns={getColumns(handleEditCustomField, handleDelete)}
        data={data}
        rowKey="id"
        currentPage={pagination?.currentPage}
        totalPages={pagination?.totalPages}
        itemsPerPage={pagination?.itemsPerPage}
        totalItems={pagination?.totalItems}
        onPageChange={(page) => setPage(page)}
      />

      {/* Responsive Styles */}
      <style>{`
        /* Custom Header Styles */

        .text-muted-subtitle {
          font-size: 0.9rem;
          color: #6c757d;
        }

        @media (max-width: 768px) {
          /* Header Responsive */

          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 1rem;
          }

          h2.dashboard-title {
            font-size: 1.35rem;
          }

          .btn-sm {
            padding: 0.375rem 0.5rem;
            font-size: 0.8rem;
          }
          
          .d-flex.gap-2 {
            gap: 0.5rem !important;
          }
          
          .table-responsive {
            font-size: 0.875rem;
          }
          
          .btn-outline-secondary,
          .btn-outline-info,
          .btn-outline-warning {
            padding: 0.375rem 0.5rem;
          }
        }
        
        @media (max-width: 480px) {
          .d-flex.gap-2 {
            flex-wrap: wrap;
            width: 100%;
          }
          
          .btn {
            padding: 0.5rem;
            font-size: 0.75rem;
          }

          .d-flex.justify-content-between.align-items-center {
            align-items: flex-start !important;
          }

          h2.dashboard-title {
            font-size: 1.1rem;
          }

          .text-muted-subtitle {
            font-size: 0.8rem;
          }
        }

        /* Filter Panel Responsive */
        @media (max-width: 768px) {
          [style*="maxWidth: 400px"] {
            max-width: 100% !important;
          }
        }
      `}</style>
    </>
  );
};

export default CustomFieldList;